/**
 * Privacy Policy Page
 * Data privacy and protection information for CarbonRoute
 */
import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-600 to-emerald-600 py-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/login" className="text-sky-200 hover:text-white text-sm mb-4 inline-flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Login
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-4">Privacy Policy</h1>
          <p className="text-sky-100 mt-2">Last updated: December 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Arantree Consulting Services ("we," "us," or "our") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when
              you use CarbonRoute, our supply chain emission calculator service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.1 Personal Information</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              When you create an account, we may collect:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Email address</li>
              <li>Full name</li>
              <li>Company name (optional)</li>
              <li>Google account information (if using Google Sign-In)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.2 Usage Data</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              We automatically collect certain information when you use our service:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Calculation history and route data you input</li>
              <li>Login timestamps and session information</li>
              <li>Browser type and device information</li>
              <li>IP address</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.3 Transportation Data</h3>
            <p className="text-gray-600 leading-relaxed">
              To provide emission calculations, we collect the transportation data you enter, including
              origin and destination locations, cargo weights, transport modes, and vehicle types.
            </p>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We use the collected information to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Provide and maintain our emission calculation service</li>
              <li>Process your calculations and generate reports</li>
              <li>Send you important service updates and notifications</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Improve our service and develop new features</li>
              <li>Monitor usage patterns and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* Data Storage */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Data Storage and Security</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We implement appropriate security measures to protect your data:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>All data is encrypted in transit using HTTPS/TLS</li>
              <li>Passwords are hashed and never stored in plain text</li>
              <li>Access to user data is restricted to authorized personnel</li>
              <li>Regular security audits and updates are performed</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Your data is stored on secure servers. While we strive to protect your information, no method
              of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information only in these circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> With third-party services that help us operate (e.g., Google Maps API for routing)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Third-Party Services</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              CarbonRoute uses the following third-party services:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Google Maps API:</strong> For route calculations and distance measurements</li>
              <li><strong>Google OAuth:</strong> For optional Google Sign-In authentication</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Export:</strong> Request your data in a portable format</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              To exercise these rights, please contact us at admin@arantree.com.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Cookies and Local Storage</h2>
            <p className="text-gray-600 leading-relaxed">
              We use browser local storage to maintain your session and store your authentication token.
              This is essential for the service to function. We do not use tracking cookies or third-party
              advertising cookies.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your account information and calculation history for as long as your account is active.
              If you request account deletion, we will remove your personal data within 30 days, except where
              we are required to retain certain information for legal or legitimate business purposes.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              CarbonRoute is not intended for users under 18 years of age. We do not knowingly collect
              personal information from children. If we become aware that we have collected data from a child,
              we will take steps to delete that information.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any significant changes
              by posting the new policy on this page and updating the "Last updated" date. We encourage you to
              review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Arantree Consulting Services</p>
              <p className="text-gray-600">Email: admin@arantree.com</p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <Link
            to="/terms"
            className="text-sky-600 hover:text-sky-700 font-medium"
          >
            View Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
